import React, { useEffect, useRef, useState } from 'react';
import Fonts from './shared/Fonts';
import Countdown from './shared/Countdown';
import {
  FloralCorner,
  GoldDivider,
  WeddingRings,
  Laurel,
  RosetteDivider,
} from './shared/Decorations';
import {
  IconShirt,
  IconGift,
  IconCamera,
  IconChurch,
  IconMapPin,
  IconBook,
  IconClock,
} from '../components/ui/Icons';
import Gallery from './shared/Gallery';
import AudioPlayer from '../components/AudioPlayer';

/* ==============================================================
   PALETA Y TIPOGRAFÍA
   ============================================================== */
const GOLD = '#a67c3d';
const GOLD_SOFT = '#c9a86a';
const GOLD_LIGHT = '#e0c893';
const CREAM = '#fdfbf6';
const CREAM_MID = '#f9f3e6';
const CREAM_DARK = '#f0e5cc';
const DARK = '#1a1a1a';
const INK = '#3d3328';
const MUTED = '#6b6255';
const NIGHT = '#1c1610'; // fondo oscuro cálido para secciones de contraste
const NIGHT_MID = '#2b2117';

const serif = "'Playfair Display', serif";
const script = "'Cormorant Garamond', serif";

const IconCityHall = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21h16M4 21V10l8-4 8 4v11M9 21v-6h6v6" />
    <path d="M12 4V2M9 6.5h6" />
    <circle cx="12" cy="13" r="1" fill="currentColor" stroke="none" />
  </svg>
);

/* ==============================================================
   ESTILOS Y ANIMACIONES
   ============================================================== */
const ClasicaStyles = () => (
  <style>{`
    /* Entrada orquestada del hero */
    @keyframes bc-rise {
      from { opacity: 0; transform: translateY(26px); filter: blur(6px); }
      to   { opacity: 1; transform: none; filter: blur(0); }
    }
    .bc-rise {
      opacity: 0;
      animation: bc-rise 1.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
      animation-delay: var(--rd, 0s);
    }

    /* Zoom lento de la foto de fondo */
    @keyframes bc-kenburns { from { transform: scale(1.12); } to { transform: scale(1); } }
    .bc-kenburns { animation: bc-kenburns 22s ease-out forwards; }

    /* Polvo dorado */
    @keyframes bc-twinkle {
      0%, 100% { opacity: 0;    transform: translateY(0) scale(0.4); }
      50%      { opacity: 0.95; transform: translateY(-16px) scale(1); }
    }
    .bc-spark {
      position: absolute;
      border-radius: 9999px;
      background: radial-gradient(circle, #fff 0%, ${GOLD_LIGHT} 45%, transparent 72%);
      animation: bc-twinkle var(--t) ease-in-out infinite;
      animation-delay: var(--d);
    }

    /* Brillo respirando detrás de la foto */
    @keyframes bc-breathe {
      0%, 100% { opacity: 0.35; transform: scale(1); }
      50%      { opacity: 0.6;  transform: scale(1.06); }
    }
    .bc-breathe { animation: bc-breathe 6s ease-in-out infinite; }

    /* Texto en oro con destello que recorre las letras */
    @keyframes bc-shine {
      from { background-position: 0% 50%; }
      to   { background-position: 250% 50%; }
    }
    .bc-gold-text, .bc-gold-text-light {
      background-size: 250% 100%;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      animation: bc-shine 8s linear infinite;
    }
    .bc-gold-text {
      background-image: linear-gradient(100deg, #8f6a2c 0%, #b8903f 22%, #d9b767 38%, #b8903f 54%, #8f6a2c 75%, #8f6a2c 100%);
    }
    .bc-gold-text-light {
      background-image: linear-gradient(100deg, ${GOLD_SOFT} 0%, #f6e7c1 30%, #fff8e3 42%, #e0c893 58%, ${GOLD_SOFT} 100%);
    }

    /* Tarjetas: elevación suave */
    .bc-card {
      transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.45s ease, border-color 0.45s ease;
    }
    .bc-card:hover {
      transform: translateY(-6px);
      border-color: ${GOLD}70 !important;
      box-shadow: 0 34px 70px -30px ${GOLD}70 !important;
    }
    .bc-card:hover .bc-icon { transform: scale(1.08); }
    .bc-icon { transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1); }

    /* Accesibilidad */
    @media (prefers-reduced-motion: reduce) {
      .bc-kenburns, .bc-spark, .bc-breathe, .bc-gold-text, .bc-gold-text-light { animation: none !important; }
      .bc-spark { display: none; }
      .bc-rise { animation: none !important; opacity: 1; filter: none; transform: none; }
      .bc-card:hover { transform: none; }
    }
  `}</style>
);

/* ==============================================================
   REVEAL AL HACER SCROLL
   ============================================================== */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return [ref, visible];
}

const REVEAL_FROM = {
  up: 'translateY(30px)',
  left: 'translateX(-40px)',
  right: 'translateX(40px)',
  zoom: 'scale(0.94)',
};

function Reveal({ children, delay = 0, className = '', from = 'up' }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : REVEAL_FROM[from] || REVEAL_FROM.up,
        transition: `opacity 1000ms ease ${delay}ms, transform 1000ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ==============================================================
   BARRA DE PROGRESO DORADA
   ============================================================== */
function ScrollProgress() {
  const ref = useRef(null);
  useEffect(() => {
    let raf;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        const p = h > 0 ? Math.min(1, window.scrollY / h) : 0;
        if (ref.current) ref.current.style.transform = `scaleX(${p})`;
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 h-[3px] pointer-events-none" style={{ zIndex: 60 }} aria-hidden="true">
      <div
        ref={ref}
        className="h-full origin-left"
        style={{
          transform: 'scaleX(0)',
          background: `linear-gradient(90deg, ${GOLD_SOFT}, ${GOLD}, ${GOLD_LIGHT})`,
        }}
      />
    </div>
  );
}

/* ==============================================================
   POLVO DORADO
   ============================================================== */
const GoldDust = ({ count = 16, z = 10 }) => {
  const [dots, setDots] = useState([]);
  useEffect(() => {
    setDots(
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: 3 + Math.random() * 94,
        top: 5 + Math.random() * 85,
        size: 3 + Math.random() * 5,
        t: 3.5 + Math.random() * 4.5,
        d: Math.random() * 7,
      }))
    );
  }, [count]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: z }} aria-hidden="true">
      {dots.map((s) => (
        <span
          key={s.id}
          className="bc-spark"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            '--t': `${s.t}s`,
            '--d': `${s.d}s`,
          }}
        />
      ))}
    </div>
  );
};

/* ==============================================================
   HELPERS DE DISEÑO
   ============================================================== */
// Título de sección: kicker en cursiva + título con oro + divisor
const SectionTitle = ({ kicker, children, light = false, className = 'mb-14' }) => (
  <Reveal className={`text-center ${className}`}>
    {kicker && (
      <p
        className="text-xl sm:text-2xl italic mb-2"
        style={{ fontFamily: script, color: light ? GOLD_LIGHT : GOLD, fontWeight: 500 }}
      >
        {kicker}
      </p>
    )}
    <h2
      className={`text-4xl sm:text-5xl leading-tight ${light ? 'bc-gold-text-light' : 'bc-gold-text'}`}
      style={{ fontFamily: serif, fontWeight: 500, letterSpacing: '-0.01em' }}
    >
      {children}
    </h2>
    <GoldDivider color={light ? GOLD_LIGHT : GOLD} className="w-52 h-6 mx-auto mt-6 opacity-85" />
  </Reveal>
);

const cardBase = {
  background: '#fff',
  border: `1px solid ${GOLD}30`,
  boxShadow: `0 20px 50px -30px ${GOLD}60`,
};

const IconCircle = ({ children, size = 'w-20 h-20', inner = 'w-9 h-9', className = '' }) => (
  <div
    className={`bc-icon ${size} rounded-full grid place-items-center ${className}`}
    style={{
      background: `linear-gradient(135deg, ${CREAM} 0%, ${CREAM_MID} 100%)`,
      border: `1px solid ${GOLD}50`,
      boxShadow: `0 0 0 6px ${CREAM}, 0 10px 24px -10px ${GOLD}70`,
      color: GOLD,
    }}
  >
    <div className={inner}>{children}</div>
  </div>
);

const TimePill = ({ children, light = false }) => (
  <p
    className="inline-block text-lg px-5 py-1.5 rounded-full italic"
    style={{
      fontFamily: script,
      fontWeight: 600,
      color: light ? GOLD_LIGHT : GOLD,
      background: light ? 'rgba(224,200,147,0.1)' : `${GOLD}12`,
      border: `1px solid ${light ? 'rgba(224,200,147,0.3)' : `${GOLD}30`}`,
    }}
  >
    {children}
  </p>
);

const CeremonyCard = ({ icon, label, name, address, time, from, delay }) => (
  <Reveal from={from} delay={delay} className="h-full">
    <div
      className="bc-card relative h-full text-center px-8 pt-16 pb-12 rounded-t-[220px] rounded-b-3xl"
      style={cardBase}
    >
      {/* Segundo filo interior */}
      <div
        className="absolute inset-2 rounded-t-[212px] rounded-b-[20px] pointer-events-none"
        style={{ border: `1px solid ${GOLD}22` }}
        aria-hidden="true"
      />
      <div className="relative">
        <IconCircle className="mx-auto mb-7">{icon}</IconCircle>
        <p className="text-2xl italic mb-4" style={{ fontFamily: script, color: GOLD, fontWeight: 600 }}>
          {label}
        </p>
        <div className="mx-auto mb-5 h-px w-16" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        {name && (
          <p className="text-2xl sm:text-[28px] mb-2 leading-snug" style={{ fontFamily: serif, color: DARK, fontWeight: 500 }}>
            {name}
          </p>
        )}
        {address && (
          <p className="text-base sm:text-lg mb-5 leading-relaxed" style={{ fontFamily: script, color: MUTED, fontWeight: 500 }}>
            {address}
          </p>
        )}
        {time && <TimePill>{time}</TimePill>}
      </div>
    </div>
  </Reveal>
);

const DetailCard = ({ icon, label, children, delay = 0 }) => (
  <Reveal delay={delay} className="h-full">
    <div
      className="bc-card text-center p-10 rounded-2xl flex flex-col items-center justify-center h-full min-h-[280px]"
      style={cardBase}
    >
      <IconCircle className="mb-7">{icon}</IconCircle>
      <p className="text-xl italic mb-3" style={{ fontFamily: script, color: GOLD, fontWeight: 600 }}>
        {label}
      </p>
      {children}
    </div>
  </Reveal>
);

/* ==============================================================
   COMPONENTE PRINCIPAL
   ============================================================== */
export default function BodaClasica({ data, title, media = [], audio = null, cover = null }) {
  // Detecta si la portada es vertical u horizontal
  const [portrait, setPortrait] = useState(null);
  useEffect(() => {
    if (!cover?.url) return;
    const im = new Image();
    im.onload = () => setPortrait(im.naturalHeight >= im.naturalWidth);
    im.src = cover.url;
  }, [cover?.url]);

  const d = data || {};
  const bride = d.bride || 'María';
  const groom = d.groom || 'Carlos';
  const date = d.date ? new Date(d.date) : null;

  const dateParts = date
    ? {
        weekday: date.toLocaleDateString('es-PE', { weekday: 'long' }),
        day: date.getDate(),
        month: date.toLocaleDateString('es-PE', { month: 'long' }),
        year: date.getFullYear(),
      }
    : null;

  const dateTiny = date
    ? date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const hasCover = !!cover?.url;
  const isLandscape = portrait === false;
  const hasChurch = !!(d.church || d.churchAddress);
  const hasCivil = !!(d.civilPlace || d.civilAddress);
  const hasReception = !!(d.place || d.address);

  const verse = d.verse || 'El amor es paciente, es bondadoso; todo lo cree, todo lo espera, todo lo soporta.';
  const verseRef = d.verseRef || '1 Corintios 13:4-7';

  // Colores del hero según haya foto o no
  const heroText = hasCover ? '#ffffff' : DARK;
  const heroAccent = hasCover ? 'rgba(255,255,255,0.92)' : GOLD;
  const heroLine = hasCover ? 'rgba(255,255,255,0.55)' : GOLD;
  const textShadow = (blur, a = 0.45) => (hasCover ? `0 4px ${blur}px rgba(0,0,0,${a})` : 'none');
  const rise = (s) => ({ '--rd': `${s}s` });

  const detailsCount = [d.dressCode, d.giftRegistry, d.hashtag].filter(Boolean).length;
  const detailsWidth = detailsCount === 1 ? 'max-w-md' : detailsCount === 2 ? 'max-w-3xl' : 'max-w-5xl';
  const detailsCols =
    detailsCount === 1
      ? 'grid-cols-1'
      : detailsCount === 2
      ? 'grid-cols-1 sm:grid-cols-2'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  const nameStyle = {
    fontFamily: serif,
    color: heroText,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    textShadow: textShadow(40),
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden" style={{ background: CREAM, color: DARK }}>
      <Fonts for="boda" />
      <ClasicaStyles />
      <ScrollProgress />

      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="relative overflow-hidden min-h-[100svh] flex items-center">
        {/* ---------- Fondo ---------- */}
        {hasCover ? (
          <>
            {/* Móvil + foto vertical: pantalla completa */}
            <img
              src={cover.url}
              alt=""
              className={`bc-kenburns absolute inset-0 w-full h-full object-cover ${isLandscape ? 'hidden' : 'lg:hidden'}`}
              style={{ objectPosition: 'center 25%' }}
            />
            {/* Fondo desenfocado con la misma foto */}
            <img
              src={cover.url}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover scale-110 blur-2xl ${
                isLandscape ? 'block' : 'hidden lg:block'
              }`}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(20,15,5,0.5) 0%, rgba(20,15,5,0.3) 30%, rgba(20,15,5,0.3) 65%, rgba(20,15,5,0.55) 100%)',
              }}
            />
          </>
        ) : (
          <>
            <div
              className="absolute inset-0"
              style={{
                background: `radial-gradient(ellipse at 50% 20%, ${CREAM} 0%, ${CREAM_MID} 45%, ${CREAM_DARK} 100%)`,
              }}
            />
            <FloralCorner color={GOLD} className="absolute top-0 left-0 w-40 h-40 sm:w-56 sm:h-56 opacity-60" />
            <FloralCorner color={GOLD} className="absolute top-0 right-0 w-40 h-40 sm:w-56 sm:h-56 -scale-x-100 opacity-60" />
            <FloralCorner color={GOLD} className="absolute bottom-16 left-0 w-32 h-32 sm:w-44 sm:h-44 opacity-40 -scale-y-100" />
            <FloralCorner color={GOLD} className="absolute bottom-16 right-0 w-32 h-32 sm:w-44 sm:h-44 opacity-40 -scale-100" />
          </>
        )}

        <GoldDust count={hasCover ? 18 : 12} />

        {/* Marco doble */}
        <div
          className="absolute inset-3 sm:inset-5 pointer-events-none hidden sm:block"
          style={{ border: `1px solid ${hasCover ? 'rgba(255,255,255,0.28)' : `${GOLD}50`}`, zIndex: 11 }}
        />
        <div
          className="absolute inset-5 sm:inset-7 pointer-events-none hidden sm:block"
          style={{ border: `1px solid ${hasCover ? 'rgba(255,255,255,0.14)' : `${GOLD}2a`}`, zIndex: 11 }}
        />

        {/* ---------- Contenido ---------- */}
        <div
          className={`relative z-20 w-full max-w-[90rem] mx-auto px-6 pt-20 pb-28 sm:pb-32 lg:py-16 grid items-center gap-10 lg:gap-14 ${
            hasCover ? (isLandscape ? 'lg:grid-cols-[1.6fr_1fr]' : 'lg:grid-cols-2') : ''
          }`}
        >
          {/* Foto completa con marco */}
          {hasCover && (
            <div
              className={`bc-rise justify-center px-2 ${isLandscape ? 'flex' : 'hidden lg:flex'}`}
              style={rise(0.1)}
            >
              <div
                className={`relative ${isLandscape ? 'w-full' : 'w-fit'}`}
                style={isLandscape ? { maxWidth: 860 } : undefined}
              >
                {/* Resplandor dorado */}
                <div
                  className="bc-breathe absolute -inset-10 rounded-full blur-3xl pointer-events-none"
                  style={{ background: `radial-gradient(circle, ${GOLD_LIGHT}66 0%, transparent 70%)` }}
                  aria-hidden="true"
                />
                {/* Marco desfasado */}
                <div
                  className="absolute inset-0 rounded-[24px] translate-x-3 translate-y-3 sm:translate-x-4 sm:translate-y-4"
                  style={{ border: `1.5px solid ${GOLD_LIGHT}`, opacity: 0.85 }}
                  aria-hidden="true"
                />

                {isLandscape ? (
                  <div
                    className="relative w-full aspect-[4/3] max-h-[80vh] overflow-hidden rounded-[24px]"
                    style={{
                      boxShadow: '0 30px 70px -20px rgba(0,0,0,0.6)',
                      border: '6px solid rgba(255,255,255,0.92)',
                    }}
                  >
                    <img
                      src={cover.url}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl"
                    />
                    <img
                      src={cover.url}
                      alt={`${bride} y ${groom}`}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <img
                    src={cover.url}
                    alt={`${bride} y ${groom}`}
                    className="relative block w-auto h-auto max-w-full max-h-[80vh] object-contain rounded-[24px]"
                    style={{
                      boxShadow: '0 30px 70px -20px rgba(0,0,0,0.6)',
                      border: '6px solid rgba(255,255,255,0.92)',
                    }}
                  />
                )}

                {/* Esquinas doradas */}
                {[
                  '-top-3 -left-3 border-t-2 border-l-2',
                  '-top-3 -right-3 border-t-2 border-r-2',
                  '-bottom-3 -left-3 border-b-2 border-l-2',
                  '-bottom-3 -right-3 border-b-2 border-r-2',
                ].map((c) => (
                  <span
                    key={c}
                    className={`absolute w-7 h-7 pointer-events-none ${c}`}
                    style={{ borderColor: GOLD_LIGHT }}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Texto */}
          <div className="text-center max-w-3xl mx-auto w-full">
            {/* Etiqueta superior */}
            <div className="bc-rise mb-6 flex items-center justify-center gap-4 sm:gap-5" style={rise(0.2)}>
              <div className="h-px w-10 sm:w-16" style={{ background: heroLine, opacity: 0.6 }} />
              <p
                className="text-2xl sm:text-3xl italic"
                style={{ fontFamily: script, color: heroAccent, fontWeight: 500 }}
              >
                Nuestra boda
              </p>
              <div className="h-px w-10 sm:w-16" style={{ background: heroLine, opacity: 0.6 }} />
            </div>

            <div className="bc-rise mb-7" style={rise(0.3)}>
              <WeddingRings color={hasCover ? 'rgba(255,255,255,0.95)' : GOLD} className="w-16 h-11 mx-auto" />
            </div>

            <h1
              className="bc-rise text-5xl sm:text-7xl lg:text-7xl xl:text-8xl leading-[0.98] mb-2"
              style={{ ...rise(0.6), ...nameStyle }}
            >
              {bride}
            </h1>

            <div className="bc-rise my-4 flex items-center justify-center gap-5 sm:gap-6" style={rise(0.9)}>
              <div className="h-px w-14 sm:w-20" style={{ background: heroLine, opacity: 0.7 }} />
              <span
                className="text-4xl sm:text-5xl italic leading-none"
                style={{ fontFamily: script, color: heroAccent, fontWeight: 500 }}
              >
                &amp;
              </span>
              <div className="h-px w-14 sm:w-20" style={{ background: heroLine, opacity: 0.7 }} />
            </div>

            <h1
              className="bc-rise text-5xl sm:text-7xl lg:text-7xl xl:text-8xl leading-[0.98] mb-9"
              style={{ ...rise(1.15), ...nameStyle }}
            >
              {groom}
            </h1>

            <div className="bc-rise" style={rise(1.5)}>
              {/* Fecha en formato "save the date" */}
              {dateParts && (
                <div className="mb-8">
                  <p
                    className="text-xl sm:text-2xl italic mb-4"
                    style={{ fontFamily: script, color: heroAccent, fontWeight: 500 }}
                  >
                    Nos casamos el
                  </p>
                  <div className="flex items-center justify-center gap-4 sm:gap-6">
                    <span
                      className="py-2 text-xs sm:text-sm uppercase tracking-[0.3em]"
                      style={{
                        fontFamily: serif,
                        color: heroText,
                        fontWeight: 500,
                        borderTop: `1px solid ${heroLine}`,
                        borderBottom: `1px solid ${heroLine}`,
                        textShadow: textShadow(20, 0.4),
                      }}
                    >
                      {dateParts.weekday}
                    </span>
                    <span
                      className="text-6xl sm:text-7xl leading-none"
                      style={{
                        fontFamily: serif,
                        color: hasCover ? '#ffffff' : GOLD,
                        fontWeight: 500,
                        textShadow: textShadow(30),
                      }}
                    >
                      {dateParts.day}
                    </span>
                    <span
                      className="py-2 text-xs sm:text-sm uppercase tracking-[0.3em] leading-relaxed"
                      style={{
                        fontFamily: serif,
                        color: heroText,
                        fontWeight: 500,
                        borderTop: `1px solid ${heroLine}`,
                        borderBottom: `1px solid ${heroLine}`,
                        textShadow: textShadow(20, 0.4),
                      }}
                    >
                      {dateParts.month}
                      <br />
                      {dateParts.year}
                    </span>
                  </div>
                </div>
              )}

              {/* Lugar + hora */}
              {(d.church || d.civilPlace || d.place || d.time) && (
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8">
                  {(d.church || d.civilPlace || d.place) && (
                    <div
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md"
                      style={{
                        background: hasCover ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.75)',
                        border: hasCover ? '1px solid rgba(255,255,255,0.25)' : `1px solid ${GOLD}45`,
                        color: hasCover ? '#ffffff' : INK,
                      }}
                    >
                      <IconMapPin className="w-3.5 h-3.5" />
                      <span className="text-sm sm:text-base font-medium" style={{ fontFamily: script }}>
                        {d.church || d.civilPlace || d.place}
                      </span>
                    </div>
                  )}
                  {d.time && (
                    <div
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md"
                      style={{
                        background: hasCover ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.75)',
                        border: hasCover ? '1px solid rgba(255,255,255,0.25)' : `1px solid ${GOLD}45`,
                        color: hasCover ? '#ffffff' : INK,
                      }}
                    >
                      <IconClock className="w-3.5 h-3.5" />
                      <span className="text-sm sm:text-base font-medium" style={{ fontFamily: script }}>
                        {d.time}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {title && (
                <p
                  className="mb-8 text-lg sm:text-xl italic max-w-xl mx-auto leading-relaxed"
                  style={{ fontFamily: script, color: hasCover ? 'rgba(255,255,255,0.85)' : MUTED, fontWeight: 500 }}
                >
                  "{title}"
                </p>
              )}

              {/* Botón de scroll */}
              <button
                type="button"
                onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                className="group inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.3em] uppercase transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 rounded-full"
                style={{ color: hasCover ? 'rgba(255,255,255,0.95)' : GOLD, outlineColor: GOLD_LIGHT }}
              >
                <span>Desliza</span>
                <span
                  className="w-9 h-9 rounded-full grid place-items-center"
                  style={{ border: `1px solid ${hasCover ? 'rgba(255,255,255,0.45)' : `${GOLD}70`}` }}
                >
                  <svg viewBox="0 0 24 24" className="w-3 h-3 animate-bounce" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Fade al fondo crema */}
        <div
          className="absolute bottom-0 left-0 right-0 h-20 sm:h-28 pointer-events-none"
          style={{
            zIndex: 12,
            background: `linear-gradient(180deg, transparent 0%, ${CREAM}55 55%, ${CREAM} 100%)`,
          }}
        />
      </section>

      {/* ============================================================
          VERSÍCULO
          ============================================================ */}
      <section
        className="relative px-6 py-24 sm:py-28"
        style={{ background: `linear-gradient(180deg, ${CREAM} 0%, ${CREAM_MID} 50%, ${CREAM} 100%)` }}
      >
        <Reveal className="max-w-3xl mx-auto" from="zoom">
          <div
            className="relative rounded-2xl px-8 sm:px-20 py-20 text-center"
            style={{
              background: '#fff',
              border: `1px solid ${GOLD}35`,
              boxShadow: `0 40px 90px -45px ${GOLD}80`,
            }}
          >
            {/* Filo interior */}
            <div
              className="absolute inset-3 rounded-xl pointer-events-none"
              style={{ border: `1px solid ${GOLD}22` }}
              aria-hidden="true"
            />
            {/* Esquinas */}
            <div className="absolute top-5 left-5 w-7 h-7 border-t border-l" style={{ borderColor: `${GOLD}80` }} />
            <div className="absolute top-5 right-5 w-7 h-7 border-t border-r" style={{ borderColor: `${GOLD}80` }} />
            <div className="absolute bottom-5 left-5 w-7 h-7 border-b border-l" style={{ borderColor: `${GOLD}80` }} />
            <div className="absolute bottom-5 right-5 w-7 h-7 border-b border-r" style={{ borderColor: `${GOLD}80` }} />

            {/* Sello */}
            <div className="absolute -top-7 left-1/2 -translate-x-1/2">
              <div
                className="w-14 h-14 rounded-full grid place-items-center"
                style={{
                  background: CREAM,
                  border: `1px solid ${GOLD}60`,
                  boxShadow: `0 10px 24px -8px ${GOLD}70, 0 0 0 5px ${CREAM}`,
                }}
              >
                <WeddingRings color={GOLD} className="w-7 h-6" />
              </div>
            </div>

            <div className="text-8xl leading-none mb-2 select-none" style={{ color: GOLD, fontFamily: serif, opacity: 0.22 }}>
              “
            </div>

            <p
              className="text-2xl sm:text-3xl leading-[1.55] italic"
              style={{ fontFamily: script, color: INK, fontWeight: 500 }}
            >
              {verse}
            </p>

            <GoldDivider color={GOLD} className="w-44 h-6 mx-auto my-9 opacity-85" />

            <p className="text-xl italic" style={{ color: GOLD, fontFamily: script, fontWeight: 600 }}>
              {verseRef}
            </p>
          </div>
        </Reveal>
      </section>

      {/* ============================================================
          CUENTA REGRESIVA
          ============================================================ */}
      {date && (
        <section className="px-6 py-20 sm:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <Reveal>
              <Laurel color={GOLD} className="w-32 h-16 mx-auto opacity-80" />
              <p className="mt-4 text-xl sm:text-2xl italic" style={{ fontFamily: script, color: GOLD, fontWeight: 500 }}>
                Faltan
              </p>
              <h2
                className="mt-1 mb-12 text-4xl sm:text-5xl bc-gold-text"
                style={{ fontFamily: serif, fontWeight: 500, letterSpacing: '-0.01em' }}
              >
                para nuestro gran día
              </h2>
            </Reveal>

            <Reveal delay={150}>
              <Countdown target={date} accent={GOLD} labelColor="#7c6b4f" />
            </Reveal>

            <Reveal delay={250}>
              <Laurel color={GOLD} className="w-32 h-16 mx-auto mt-12 opacity-80 rotate-180" />
            </Reveal>
          </div>
        </section>
      )}

      {/* ============================================================
          NUESTRA HISTORIA
          ============================================================ */}
      {d.story && (
        <section className="px-6 py-20 sm:py-24" style={{ background: `linear-gradient(180deg, ${CREAM} 0%, ${CREAM_MID} 100%)` }}>
          <div className="max-w-2xl mx-auto text-center">
            <Reveal>
              <div className="flex justify-center mb-6">
                <IconCircle size="w-16 h-16" inner="w-7 h-7">
                  <IconBook className="w-full h-full" />
                </IconCircle>
              </div>
            </Reveal>
            <SectionTitle kicker="Cómo empezó todo" className="mb-10">
              Nuestra historia
            </SectionTitle>
            <Reveal delay={100}>
              <p
                className="text-lg sm:text-xl leading-[2] italic whitespace-pre-line"
                style={{ fontFamily: script, color: INK, fontWeight: 500 }}
              >
                {d.story}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* ============================================================
          GALERÍA
          ============================================================ */}
      <Gallery
        media={media}
        category="Matrimonios"
        accent={GOLD}
        text={DARK}
        muted={MUTED}
        title="Nuestros Momentos"
        subtitle="Recuerdos"
        headingFont={serif}
      />

      {/* ============================================================
          CEREMONIAS
          ============================================================ */}
      {(hasCivil || hasChurch) && (
        <section className="px-6 py-24 sm:py-28">
          <div className="max-w-5xl mx-auto">
            <SectionTitle kicker="Itinerario">Ceremonias</SectionTitle>

            <div className={`grid ${hasCivil && hasChurch ? 'md:grid-cols-2' : ''} gap-8 md:gap-10 max-w-3xl mx-auto`}>
              {hasCivil && (
                <CeremonyCard
                  icon={<IconCityHall className="w-full h-full" />}
                  label="Ceremonia civil"
                  name={d.civilPlace}
                  address={d.civilAddress}
                  time={d.civilTime}
                  from={hasChurch ? 'left' : 'up'}
                />
              )}
              {hasChurch && (
                <CeremonyCard
                  icon={<IconChurch className="w-full h-full" />}
                  label="Ceremonia religiosa"
                  name={d.church}
                  address={d.churchAddress}
                  time={d.churchTime}
                  from={hasCivil ? 'right' : 'up'}
                  delay={120}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          RECEPCIÓN (sección oscura de contraste)
          ============================================================ */}
      {hasReception && (
        <section
          className="relative px-6 py-24 sm:py-32 overflow-hidden"
          style={{ background: `radial-gradient(ellipse at 50% 0%, ${NIGHT_MID} 0%, ${NIGHT} 70%)` }}
        >
          <GoldDust count={22} z={1} />
          <div
            className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${GOLD}33 0%, transparent 65%)` }}
            aria-hidden="true"
          />

          <Reveal className="relative max-w-3xl mx-auto text-center" from="zoom">
            <RosetteDivider color={GOLD_LIGHT} className="w-64 h-8 mx-auto mb-10 opacity-85" />

            <div className="flex justify-center mb-8">
              <div
                className="bc-icon w-20 h-20 rounded-full grid place-items-center"
                style={{
                  background: 'rgba(224,200,147,0.08)',
                  border: `1px solid ${GOLD_SOFT}80`,
                  boxShadow: `0 0 40px -6px ${GOLD}90`,
                  color: GOLD_LIGHT,
                }}
              >
                <div className="w-9 h-9">
                  <IconMapPin className="w-full h-full" />
                </div>
              </div>
            </div>

            <p className="text-xl sm:text-2xl italic mb-2" style={{ fontFamily: script, color: GOLD_LIGHT, fontWeight: 500 }}>
              Celebremos juntos en
            </p>

            {d.place && (
              <h2
                className="text-4xl sm:text-6xl mb-5 leading-tight bc-gold-text-light"
                style={{ fontFamily: serif, fontWeight: 500, letterSpacing: '-0.01em' }}
              >
                {d.place}
              </h2>
            )}

            {d.address && (
              <p
                className="text-lg sm:text-xl mb-8 leading-relaxed max-w-xl mx-auto"
                style={{ fontFamily: script, color: 'rgba(253,251,246,0.8)', fontWeight: 500 }}
              >
                {d.address}
              </p>
            )}

            {d.receptionTime && <TimePill light>{d.receptionTime}</TimePill>}

            <RosetteDivider color={GOLD_LIGHT} className="w-64 h-8 mx-auto mt-10 opacity-85 rotate-180" />
          </Reveal>
        </section>
      )}

      {/* ============================================================
          DETALLES
          ============================================================ */}
      {detailsCount > 0 && (
        <section className="px-6 py-24 sm:py-28">
          <div className={`${detailsWidth} mx-auto`}>
            <SectionTitle kicker="Para que todo sea perfecto">Detalles</SectionTitle>

            <div className={`grid ${detailsCols} gap-6 sm:gap-8`}>
              {d.dressCode && (
                <DetailCard icon={<IconShirt className="w-full h-full" />} label="Dress code">
                  <p className="text-xl sm:text-2xl leading-snug" style={{ fontFamily: serif, color: DARK, fontWeight: 500 }}>
                    {d.dressCode}
                  </p>
                </DetailCard>
              )}

              {d.giftRegistry && (
                <DetailCard icon={<IconGift className="w-full h-full" />} label="Mesa de regalos" delay={120}>
                  <a
                    href={d.giftRegistry}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xl sm:text-2xl underline decoration-dotted underline-offset-[6px] hover:opacity-70 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                    style={{ fontFamily: serif, color: DARK, fontWeight: 500, outlineColor: GOLD }}
                  >
                    Ver lista
                  </a>
                </DetailCard>
              )}

              {d.hashtag && (
                <DetailCard icon={<IconCamera className="w-full h-full" />} label="Comparte tus fotos" delay={240}>
                  <p className="text-xl sm:text-2xl leading-snug" style={{ fontFamily: serif, color: DARK, fontWeight: 500 }}>
                    {d.hashtag}
                  </p>
                </DetailCard>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          MENSAJE
          ============================================================ */}
      {d.description && (
        <section className="px-6 py-20 sm:py-24" style={{ background: `linear-gradient(180deg, ${CREAM} 0%, ${CREAM_MID} 100%)` }}>
          <Reveal className="max-w-2xl mx-auto text-center">
            <Laurel color={GOLD} className="w-28 h-14 mx-auto mb-6 opacity-75" />
            <p className="text-xl italic mb-6" style={{ fontFamily: script, color: GOLD, fontWeight: 600 }}>
              Un mensaje para ti
            </p>
            <p
              className="text-xl sm:text-2xl leading-[1.9] italic"
              style={{ fontFamily: script, color: INK, fontWeight: 500 }}
            >
              “{d.description}”
            </p>
          </Reveal>
        </section>
      )}

      {/* ============================================================
          NOTAS
          ============================================================ */}
      {d.notes && (
        <section className="px-6 py-12">
          <Reveal className="max-w-xl mx-auto text-center">
            <div
              className="inline-block px-7 py-5 rounded-xl"
              style={{ background: `${GOLD}0A`, border: `1px dashed ${GOLD}55` }}
            >
              <p className="text-base leading-relaxed" style={{ fontFamily: script, color: MUTED, fontWeight: 500 }}>
                {d.notes}
              </p>
            </div>
          </Reveal>
        </section>
      )}

      {/* ============================================================
          CIERRE + FOOTER (oscuro)
          ============================================================ */}
      <div
        className="relative overflow-hidden"
        style={{ background: `radial-gradient(ellipse at 50% 30%, ${NIGHT_MID} 0%, ${NIGHT} 75%)` }}
      >
        <GoldDust count={30} z={1} />
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
          style={{ background: `radial-gradient(circle, ${GOLD}2e 0%, transparent 62%)` }}
          aria-hidden="true"
        />

        <section className="relative px-6 pt-28 pb-16 text-center">
          <Reveal from="zoom">
            <WeddingRings color={GOLD_LIGHT} className="w-20 h-12 mx-auto mb-8 opacity-90" />

            <p
              className="text-5xl sm:text-7xl leading-tight bc-gold-text-light"
              style={{ fontFamily: serif, fontWeight: 600, letterSpacing: '-0.02em' }}
            >
              {bride}
              <span className="mx-4 sm:mx-6 italic font-normal" style={{ fontFamily: script }}>
                &amp;
              </span>
              {groom}
            </p>

            <GoldDivider color={GOLD_LIGHT} className="w-52 h-6 mx-auto my-9 opacity-85" />

            <p
              className="text-2xl sm:text-3xl italic max-w-xl mx-auto leading-[1.7]"
              style={{ fontFamily: script, color: 'rgba(253,251,246,0.92)', fontWeight: 500 }}
            >
              Tu presencia es el regalo más valioso.
              <br />
              <span style={{ color: GOLD_LIGHT }}>¡Te esperamos para celebrar juntos!</span>
            </p>

            {dateTiny && (
              <div className="mt-10 inline-flex items-center gap-4">
                <div className="h-px w-12" style={{ background: `${GOLD_SOFT}80` }} />
                <p className="text-lg italic" style={{ color: GOLD_LIGHT, fontFamily: script, fontWeight: 600 }}>
                  {dateTiny}
                </p>
                <div className="h-px w-12" style={{ background: `${GOLD_SOFT}80` }} />
              </div>
            )}
          </Reveal>
        </section>

        <footer className="relative pb-12 text-center">
          <FloralCorner color={GOLD_SOFT} className="w-14 h-14 mx-auto mb-5 opacity-50 rotate-180" />
          <p className="text-xs" style={{ fontFamily: script, color: 'rgba(224,200,147,0.75)', letterSpacing: '0.12em', fontWeight: 500 }}>
            Invitación creada con{' '}
            <b style={{ color: GOLD_LIGHT, fontFamily: serif, fontWeight: 700 }}>Go Fiesta</b>
          </p>
        </footer>
      </div>

      {audio && <AudioPlayer audio={audio} accent={GOLD} />}
    </div>
  );
}